/**
 * Lane Renderer
 *
 * Draws the gameplay lanes and judgment line
 */

export class LaneRenderer {
  private readonly NUM_LANES: number = 4;
  private readonly LANE_WIDTH_RATIO: number = 0.6; // 60% of canvas width
  private readonly JUDGMENT_LINE_POSITION: number = 0.8; // 80% from top

  /**
   * Render the lanes
   */
  render(ctx: CanvasRenderingContext2D): void {
    const canvas = ctx.canvas;
    const laneAreaWidth = canvas.width * this.LANE_WIDTH_RATIO;
    const laneWidth = laneAreaWidth / this.NUM_LANES;
    const laneStartX = (canvas.width - laneAreaWidth) / 2;

    // Draw lane backgrounds
    for (let i = 0; i < this.NUM_LANES; i++) {
      const x = laneStartX + (i * laneWidth);

      // Alternate lane colors for visibility
      ctx.fillStyle = i % 2 === 0 ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.02)';
      ctx.fillRect(x, 0, laneWidth, canvas.height);

      // Draw lane dividers
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }

    // Draw right edge of last lane
    ctx.beginPath();
    ctx.moveTo(laneStartX + laneAreaWidth, 0);
    ctx.lineTo(laneStartX + laneAreaWidth, canvas.height);
    ctx.stroke();

    // Draw judgment line
    this.renderJudgmentLine(ctx);

    // Draw lane key indicators
    this.renderKeyIndicators(ctx);
  }

  /**
   * Draw the judgment line where players hit notes
   */
  private renderJudgmentLine(ctx: CanvasRenderingContext2D): void {
    const canvas = ctx.canvas;
    const y = canvas.height * this.JUDGMENT_LINE_POSITION;
    const laneAreaWidth = canvas.width * this.LANE_WIDTH_RATIO;
    const laneStartX = (canvas.width - laneAreaWidth) / 2;

    // Glow effect
    ctx.shadowBlur = 15;
    ctx.shadowColor = '#00ff88';

    // Main line
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(laneStartX, y);
    ctx.lineTo(laneStartX + laneAreaWidth, y);
    ctx.stroke();

    // Reset shadow
    ctx.shadowBlur = 0;
  }

  /**
   * Draw key indicators at the bottom
   */
  private renderKeyIndicators(ctx: CanvasRenderingContext2D): void {
    const canvas = ctx.canvas;
    const laneAreaWidth = canvas.width * this.LANE_WIDTH_RATIO;
    const laneWidth = laneAreaWidth / this.NUM_LANES;
    const laneStartX = (canvas.width - laneAreaWidth) / 2;
    const y = canvas.height - 40;

    const keys = ['D', 'F', 'J', 'K'];

    ctx.font = '20px Arial';
    ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';

    for (let i = 0; i < this.NUM_LANES; i++) {
      const x = laneStartX + (i * laneWidth) + (laneWidth / 2);
      ctx.fillText(keys[i], x, y);
    }
  }

  /**
   * Get lane configuration for other systems
   */
  getLaneConfig() {
    return {
      numLanes: this.NUM_LANES,
      widthRatio: this.LANE_WIDTH_RATIO,
      judgmentLinePosition: this.JUDGMENT_LINE_POSITION
    };
  }
}
